import type * as monacoTypes from 'monaco-editor';
import type { TemplateInputProps } from '../template-input/template-input';

const BRACE_OPEN = '{{';
const BRACE_CLOSE = '}}';
const BRACE_OPEN_LENGTH = BRACE_OPEN.length;

/**
 * Finds if the cursor is inside {{ }} braces and returns the opening brace position.
 * Returns null if not inside braces or if there's a closing brace before the cursor.
 */
const findOpeningBraces = (model: monacoTypes.editor.ITextModel, position: monacoTypes.Position) => {
  const line = model.getLineContent(position.lineNumber);
  const beforeCursor = line.substring(0, position.column - 1);
  const openIdx = beforeCursor.lastIndexOf(BRACE_OPEN);
  if (openIdx === -1) return null;
  // ensure there isn't a closing before the caret
  const between = beforeCursor.substring(openIdx + BRACE_OPEN_LENGTH);
  if (between.includes(BRACE_CLOSE)) return null;
  // Return the column where {{ starts (1-based)
  const openBraceColumn = openIdx + 1;
  return { openBraceColumn };
};

/**
 * Creates a completion provider for template variables in JSON editor.
 * Provides autocomplete for {{ variable }} syntax with quote wrapping.
 */
export const createTemplateCompletionProvider = (
  monaco: typeof monacoTypes,
  autocompleteItems: TemplateInputProps['autocompleteItems'],
): monacoTypes.languages.CompletionItemProvider => {
  return {
    triggerCharacters: ['{', ' ', '.'],
    provideCompletionItems(model, position, context) {
      const word = model.getWordAtPosition(position);

      // Check if we're inside {{ }}
      const opening = findOpeningBraces(model, position);

      // Check if this is a manual trigger (Ctrl+Space)
      const isManualTrigger = context.triggerKind === monaco.languages.CompletionTriggerKind.Invoke;

      // Only show suggestions if inside braces OR manual trigger
      if (!opening && !isManualTrigger) {
        return { suggestions: [] };
      }

      let replaceStartColumn: number;
      let replaceEndColumn: number;
      let isInsideBraces = false;
      let fullReplaceStartColumn: number | undefined;

      if (opening) {
        // We're inside {{, replace from after {{ to }}
        isInsideBraces = true;
        const lineContent = model.getLineContent(position.lineNumber);
        const afterCursor = lineContent.substring(position.column - 1);
        const beforeOpening = lineContent.substring(0, opening.openBraceColumn - 1);

        // Check if there's a quote before {{
        const hasQuoteBefore = beforeOpening.endsWith('"');

        // For filtering to work correctly, the range should start from after {{ (where user types)
        // This allows Monaco to filter based on what the user typed, not including {{
        const startAfterBraces = opening.openBraceColumn + BRACE_OPEN_LENGTH;
        // Store the full start (including {{) for replacement
        fullReplaceStartColumn = hasQuoteBefore ? opening.openBraceColumn - 1 : opening.openBraceColumn;

        // Range starts from after {{ for proper filtering
        replaceStartColumn = startAfterBraces;

        // Check if there are closing braces and include them in range
        const closingMatch = afterCursor.match(/^(\s*)\}\}/);
        if (closingMatch) {
          const afterClosing = lineContent.substring(position.column - 1 + closingMatch[0].length);
          // Check if there's a quote after }}
          const hasQuoteAfter = afterClosing.startsWith('"');
          replaceEndColumn = position.column + closingMatch[0].length + (hasQuoteAfter ? 1 : 0);
        } else {
          // No closing braces found - range ends at cursor or end of word
          // Always use cursor position as the end, ensuring it's at least after the opening braces
          replaceEndColumn = Math.max(position.column, startAfterBraces);
          // If there's a word that extends beyond, use that instead
          if (word && word.endColumn > replaceEndColumn) {
            replaceEndColumn = word.endColumn;
          }
        }
      } else {
        // Not inside {{, manual trigger - just insert at cursor
        replaceStartColumn = position.column;
        replaceEndColumn = word ? word.endColumn : position.column;
      }

      // Ensure the range is always valid (endColumn >= startColumn)
      const validEndColumn = Math.max(replaceEndColumn, replaceStartColumn);

      const replaceRange: monacoTypes.IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: replaceStartColumn,
        endColumn: validEndColumn,
      };

      // Filter out empty arrays, then flatten
      const flatItems = Object.values(autocompleteItems)
        .filter(arr => arr.length > 0)
        .flat();

      const suggestions: monacoTypes.languages.CompletionItem[] = flatItems.map((tv, index) => {
        let insertText: string;
        let filterText: string;

        if (isInsideBraces) {
          // Inside {{, the range starts from after {{ for proper filtering
          // This allows Monaco to filter based on what the user typed (not including {{)
          filterText = tv.value;

          // Since range starts after {{, we need to use additionalTextEdits to replace {{
          if (fullReplaceStartColumn && fullReplaceStartColumn < replaceRange.startColumn) {
            // Range starts after {{, so insertText replaces only what's after {{
            insertText = ` ${tv.value} }}"`;

            // Use additionalTextEdits to also replace the {{ part
            // Always include the starting quote when replacing {{
            return {
              label: tv.label,
              kind: monaco.languages.CompletionItemKind.Variable,
              insertText: insertText,
              range: replaceRange,
              additionalTextEdits: [
                {
                  range: {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: fullReplaceStartColumn,
                    endColumn: replaceRange.startColumn,
                  },
                  text: `"{{`,
                },
              ],
              sortText: `${index.toString().padStart(3, '0')}`,
              filterText: filterText,
              detail: tv.detail,
            };
          } else {
            // Range includes {{, so insertText can include it normally
            insertText = `"{{ ${tv.value} }}"`;
          }
        } else {
          // Outside braces, insert full quoted template variable
          insertText = `"{{ ${tv.value} }}"`;
          filterText = tv.label;
        }

        return {
          label: tv.label, // Always show clean label without {{ }}
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: insertText,
          range: replaceRange,
          sortText: `${index.toString().padStart(3, '0')}`,
          filterText: filterText,
          detail: tv.detail,
        };
      });

      return {
        suggestions,
        // Mark as incomplete when inside braces to keep widget open
        incomplete: isInsideBraces,
      };
    },
    resolveCompletionItem(item) {
      const matched = Object.values(autocompleteItems)
        .flat()
        .find(v => v.label === item.label);
      if (matched) {
        item.documentation = {
          value: `**${matched.label}**\n\n${matched.value ?? ''}`,
          isTrusted: true,
        };
      }
      return item;
    },
  };
};

/**
 * Sets up auto-trigger behavior for template variable suggestions.
 * Triggers suggestions when typing {{ or . inside {{ }}.
 */
export const setupAutoTrigger = (editor: monacoTypes.editor.IStandaloneCodeEditor): monacoTypes.IDisposable => {
  return editor.onDidChangeModelContent(() => {
    requestAnimationFrame(() => {
      const model = editor.getModel();
      if (!model) return;

      const position = editor.getPosition();
      if (!position) return;

      const line = model.getLineContent(position.lineNumber);
      const beforeCursor = line.substring(0, position.column - 1);

      // Check if we just typed {{ (this will auto-trigger once when {{ is typed)
      if (beforeCursor.endsWith('{{')) {
        editor.trigger('json-editor', 'editor.action.triggerSuggest', {});
        return;
      }

      // Check if we're inside {{ }} and just typed a dot or space
      const openIdx = beforeCursor.lastIndexOf('{{');
      if (openIdx !== -1) {
        const between = beforeCursor.substring(openIdx + 2);
        if (!between.includes('}}')) {
          // We're inside braces
          const lastChar = beforeCursor[beforeCursor.length - 1];
          if (lastChar === '.' || lastChar === ' ') {
            // Just typed a dot or space inside braces - trigger suggestions
            const isSuggestVisible = !!document.querySelector('.suggest-widget.visible');
            if (!isSuggestVisible) {
              editor.trigger('json-editor', 'editor.action.triggerSuggest', {});
            }
          }
        }
      }
    });
  });
};
